import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/constants";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Create axios instance with interceptors for JWT
const createAuthAxios = (getTokens, refreshTokens, onLogout) => {
  const instance = axios.create({ baseURL: API });
  
  // Request interceptor - add access token
  instance.interceptors.request.use(
    (config) => {
      let token = null;
      
      // First try getTokens
      const tokens = getTokens();
      token = tokens?.access_token;
      
      // Fallback: directly check localStorage
      if (!token) {
        try {
          const storedUser = JSON.parse(localStorage.getItem("ltg_user") || '{}');
          token = storedUser?.access_token;
        } catch (e) {
          console.error("Error reading token from localStorage", e);
        }
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Response interceptor - handle 401 and refresh token
  let isRefreshing = false;
  let refreshPromise = null;

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        const tokens = getTokens();
        if (tokens?.refresh_token) {
          try {
            // Coalesce concurrent refresh attempts so only one network call happens
            if (!isRefreshing) {
              isRefreshing = true;
              refreshPromise = axios.post(`${API}/auth/refresh-token`, {
                refresh_token: tokens.refresh_token,
              }).finally(() => {
                isRefreshing = false;
              });
            }
            const res = await refreshPromise;
            const newAccessToken = res.data.access_token;
            refreshTokens(newAccessToken);
            
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return instance(originalRequest);
          } catch (refreshError) {
            // Refresh token also expired - clear session and redirect to login
            onLogout();
            // Avoid loop: only redirect if not already on the login screen
            try {
              if (typeof window !== "undefined" &&
                  window.location.pathname !== "/" &&
                  !window.location.pathname.startsWith("/login")) {
                window.location.replace("/");
              }
            } catch (_) {}
            // Tag the error so callers can suppress noisy toasts
            refreshError.isAuthExpiry = true;
            return Promise.reject(refreshError);
          }
        } else {
          // No refresh token at all -> hard logout
          onLogout();
          try {
            if (typeof window !== "undefined" &&
                window.location.pathname !== "/" &&
                !window.location.pathname.startsWith("/login")) {
              window.location.replace("/");
            }
          } catch (_) {}
          error.isAuthExpiry = true;
        }
      }
      
      return Promise.reject(error);
    }
  );
  
  return instance;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("ltg_user");
    if (stored) {
      const userData = JSON.parse(stored);
      // Check if tokens exist and set user
      setUser(userData);
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("ltg_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("ltg_user");
  };

  // Get current tokens
  const getTokens = useCallback(() => {
    // First try from user state
    if (user?.access_token) {
      return {
        access_token: user.access_token,
        refresh_token: user.refresh_token
      };
    }
    // Fallback to localStorage if user state is stale
    try {
      const storedUser = localStorage.getItem("ltg_user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return {
          access_token: parsed.access_token,
          refresh_token: parsed.refresh_token
        };
      }
    } catch (e) {
      console.error('[AUTH] Error reading tokens from localStorage:', e);
    }
    return { access_token: null, refresh_token: null };
  }, [user]);

  // Update access token after refresh
  const refreshTokens = useCallback((newAccessToken) => {
    if (user) {
      const updatedUser = { ...user, access_token: newAccessToken };
      setUser(updatedUser);
      localStorage.setItem("ltg_user", JSON.stringify(updatedUser));
    }
  }, [user]);

  // Create auth axios instance
  const authAxios = useCallback(() => {
    return createAuthAxios(getTokens, refreshTokens, logout);
  }, [getTokens, refreshTokens]);

  const refreshUser = useCallback(async () => {
    if (user && !user.is_admin) {
      try {
        const api = authAxios();
        const res = await api.get(`/user/${user.id}`);
        // Preserve tokens when refreshing user data
        const updatedUser = {
          ...res.data,
          access_token: user.access_token,
          refresh_token: user.refresh_token
        };
        setUser(updatedUser);
        localStorage.setItem("ltg_user", JSON.stringify(updatedUser));
        return updatedUser;
      } catch (e) {
        console.error("Failed to refresh user", e);
      }
    }
    return user;
  }, [user, authAxios]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, refreshUser, authAxios, getTokens }}>
      {children}
    </AuthContext.Provider>
  );
};
